import { FunctionComponent as FC } from 'react';
import { Icon, hasBankImage } from 'vopay';
import { useTranslation } from 'react-i18next';
import type { CardBrand, Icons } from 'vopay';
import { PaymentMethodType } from '#shared/hooks/useApi/usePaymentMethods';

const DEFAULT_PAYMENT_RAIL_SIZE = '40px';

interface PaymentMethodObjType {
  icon: Icons;
  height?: string;
  width?: string;
}

const creditCardIconMap: Record<CardBrand, PaymentMethodObjType> = {
  visa: {
    icon: 'cardVisa',
    height: '19px',
    width: '32px',
  },
  mastercard: {
    icon: 'cardMaster',
    height: '19px',
    width: '19px',
  },
  amex: {
    icon: 'cardAE',
    height: '19px',
    width: '19px',
  },
  diners: {
    icon: 'cardDiners',
    height: '19px',
    width: '19px',
  },
  discover: {
    icon: 'cardDiscover',
    height: '19px',
    width: '19px',
  },
  jcb: {
    icon: 'cardJcb',
    height: '19px',
    width: '19px',
  },
  uatp: {
    icon: 'creditCard',
    height: '19px',
    width: '19px',
  },
  unionpay: {
    icon: 'creditCard',
    height: '19px',
    width: '32px',
  },
  unknown: {
    icon: 'creditCard',
    height: '19px',
    width: '19px',
  },
};

const paymentRailMap: Record<string, PaymentMethodObjType> = {
  eft: {
    icon: 'eft',
    height: '19px',
    width: '36px',
  },
  ach: {
    icon: 'ach',
    height: '19px',
    width: '48px',
  },
  vopayinstant: {
    icon: 'vLogo',
    height: '19px',
    width: '19px',
  },
  fednow: {
    icon: 'fednow',
    height: '19px',
    width: '48px',
  },
  rtp: {
    icon: 'rtp',
    height: '19px',
    width: '45px',
  },
};

type ThirdPartyPaymentMethodType = Extract<PaymentMethodType, 'ApplePay' | 'GooglePay' | 'PayPal' | 'Venmo' | 'Zelle'>;

const thirdPartyIconMap: Record<ThirdPartyPaymentMethodType, PaymentMethodObjType> = {
  ApplePay: {
    icon: 'applePay',
    height: '19px',
    width: '28px',
  },
  GooglePay: {
    icon: 'googlePay',
    height: '19px',
    width: '29px',
  },
  PayPal: {
    icon: 'paypal',
    height: '19px',
    width: '19px',
  },
  Venmo: {
    icon: 'venmo',
    height: '19px',
    width: '19px',
  },
  Zelle: {
    icon: 'zelle',
    height: '19px',
    width: '19px',
  },
};

const defaultIconMap: Record<PaymentMethodType, Icons> = {
  BankAccount: 'bankDark',
  CreditCard: 'creditCard',
  Email: 'interacYellow',
  PhoneNumber: 'interacYellow',
  DebitCard: 'creditCard',
  GooglePay: 'googlePay',
  ApplePay: 'applePay',
  PayPal: 'paypal',
  Venmo: 'venmo',
  Zelle: 'zelle',
};

type LogoProps = {
  method: PaymentMethodType;
  creditCardBrand?: CardBrand;
  bankId?: string;
  size?: string;
  paymentRail?: string;
  className?: string;
};

const PaymentMethodLogo: FC<LogoProps> = ({ creditCardBrand, method, bankId, paymentRail, size, className }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.Customers.PaymentMethodsTab' });
  const fallbackIconSize = size ?? DEFAULT_PAYMENT_RAIL_SIZE;

  if (method === 'BankAccount' && bankId && hasBankImage(bankId)) {
    return (
      <img
        src={`https://embed-dev.vopay.com/images/logos-banks/${bankId}.svg`}
        alt={t('bankLogoAlt', { defaultValue: 'Bank Logo' }) ?? ''}
        className={className}
        style={{
          width: fallbackIconSize,
          height: fallbackIconSize,
        }}
      />
    );
  }

  if (paymentRail) {
    const paymentRailFromMap = paymentRailMap[paymentRail];

    return (
      <Icon
        icon={paymentRailFromMap?.icon || 'dollar'}
        className={className}
        size="xlg"
        color="primary-dark"
        width={paymentRailFromMap?.width ?? fallbackIconSize}
        height={paymentRailFromMap?.height ?? fallbackIconSize}
      />
    );
  }

  if (method === 'Email' || method === 'PhoneNumber') {
    return (
      <Icon
        icon="interacYellow"
        className={className}
        size="xlg"
        color="none"
        width={fallbackIconSize}
        height={fallbackIconSize}
      />
    );
  }

  if (['ApplePay', 'GooglePay', 'PayPal', 'Venmo', 'Zelle'].includes(method)) {
    const selectedPayment = thirdPartyIconMap[method as ThirdPartyPaymentMethodType];

    return (
      <Icon
        icon={selectedPayment.icon}
        className={className}
        size="xlg"
        color="none"
        width={selectedPayment.width ?? fallbackIconSize}
        height={selectedPayment.height ?? fallbackIconSize}
      />
    );
  }

  if (method === 'CreditCard' && creditCardBrand && creditCardIconMap[creditCardBrand]) {
    const selectedCard = creditCardIconMap[creditCardBrand];

    return (
      <Icon
        icon={selectedCard.icon}
        className={className}
        size="xlg"
        color="none"
        width={selectedCard.width ?? fallbackIconSize}
        height={selectedCard.height ?? fallbackIconSize}
      />
    );
  }

  return (
    <Icon
      icon={defaultIconMap[method] || 'dollar'}
      className={className}
      size="xlg"
      color="none"
      width={fallbackIconSize}
      height={fallbackIconSize}
    />
  );
};

export default PaymentMethodLogo;
